<script lang="ts">
type RadioOption<T = string> = {
  value: T;
  label: string;
  description?: string;
  icon?: string;
  disabled?: boolean;
};

type Props<T = string> = {
  options: RadioOption<T>[];
  value: T;
  name?: string;
  layout?: 'horizontal' | 'vertical' | 'grid';
  size?: 'sm' | 'md' | 'lg';
  variant?: 'default' | 'card';
  onchange?: (value: T) => void;
};

let {
  options,
  value = $bindable(),
  name = 'radio-group',
  layout = 'horizontal',
  size = 'md',
  variant = 'default',
  onchange
}: Props = $props();

function handleChange(newValue: typeof value) {
  value = newValue;
  if (onchange) {
    onchange(newValue);
  }
}
</script>

<div class="radio-button-group {layout} {size} {variant}">
  {#each options as option (option.value)}
    <label
      class="radio-option"
      class:selected={value === option.value}
      class:disabled={option.disabled}
    >
      <input
        type="radio"
        {name}
        value={option.value}
        checked={value === option.value}
        disabled={option.disabled}
        onchange={() => handleChange(option.value)}
      />
      <div class="option-content">
        {#if option.icon}
          <span class="option-icon">{option.icon}</span>
        {/if}
        <div class="option-text">
          <span class="option-label">{option.label}</span>
          {#if option.description}
            <span class="option-description">{option.description}</span>
          {/if}
        </div>
      </div>
    </label>
  {/each}
</div>

<style lang="scss">
.radio-button-group {
  display: flex;
  gap: var(--spacing-md);

  &.horizontal {
    flex-direction: row;
    flex-wrap: wrap;
  }

  &.vertical {
    flex-direction: column;
  }

  &.grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  }
}

.radio-option {
  position: relative;
  display: flex;
  cursor: pointer;
  user-select: none;
  transition: all var(--transition-fast);

  input[type="radio"] {
    position: absolute;
    opacity: 0;
    width: 0;
    height: 0;
  }

  &.disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

// Default variant (button style)
.radio-button-group.default .radio-option {
  .option-content {
    display: flex;
    align-items: center;
    gap: var(--spacing-sm);
    padding: var(--spacing-sm) var(--spacing-lg);
    background: var(--color-background);
    border: var(--border-width-thin) solid var(--color-border);
    border-radius: var(--radius-md);
    transition: all var(--transition-fast);
  }

  &:hover:not(.disabled) .option-content {
    background: var(--color-background-tertiary);
    border-color: var(--color-primary);
  }

  &.selected .option-content {
    background: var(--color-primary-light);
    border-color: var(--color-primary);
    color: var(--color-primary);
    font-weight: var(--font-weight-semibold);
  }
}

// Card variant (more prominent)
.radio-button-group.card .radio-option {
  flex: 1;

  .option-content {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-xs);
    padding: var(--spacing-lg);
    background: var(--color-background);
    border: var(--border-width-medium) solid var(--color-border);
    border-radius: var(--radius-lg);
    transition: all var(--transition-fast);
    width: 100%;
  }

  .option-icon {
    font-size: var(--font-size-2xl);
    margin-bottom: var(--spacing-xs);
  }

  .option-text {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-xs);
  }

  .option-label {
    font-weight: var(--font-weight-semibold);
    color: var(--color-text-primary);
  }

  .option-description {
    font-size: var(--font-size-sm);
    color: var(--color-text-secondary);
  }

  &:hover:not(.disabled) .option-content {
    border-color: var(--color-primary);
    box-shadow: var(--shadow-md);
    transform: translateY(-2px);
  }

  &.selected .option-content {
    background: var(--color-primary-light);
    border-color: var(--color-primary);
    box-shadow: var(--shadow-lg);
  }

  &.selected .option-label {
    color: var(--color-primary);
  }
}

// Size variants
.radio-button-group.sm .radio-option .option-content {
  padding: var(--spacing-xs) var(--spacing-md);
  font-size: var(--font-size-sm);
}

.radio-button-group.lg .radio-option .option-content {
  padding: var(--spacing-lg) var(--spacing-xl);
  font-size: var(--font-size-lg);
}

// Default size styling
.option-text {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.option-label {
  font-size: var(--font-size-base);
  line-height: var(--line-height-tight);
}

.option-description {
  font-size: var(--font-size-xs);
  color: var(--color-text-tertiary);
  line-height: var(--line-height-tight);
}

.option-icon {
  font-size: var(--font-size-xl);
}
</style>
